import { getTargetId } from '@/adapter/blockAdapter';
import IconButton from '@/components/common/IconButton';
import { ExtendedCustomNodeProps } from '@/components/flow/NodeRenderer';
import { useSelected } from '@/hooks/useSelected';
import { getColorFromString } from '@/util/color';
import SettingsIcon from '@mui/icons-material/Settings';
import { Card, CardHeader, Divider, Stack, Typography, useTheme } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import { FC } from 'react';

const CustomNode: FC<ExtendedCustomNodeProps> = ({ id, data, size, ...props }) => {
  const { setSelectedEdge } = useSelected();
  const theme = useTheme();

  const headerBgColor = getColorFromString(
    props.blockStyle || data.backendNode.type,
    theme.palette.mode === 'dark' ? 900 : 200
  );
  const headerTextColor = theme.palette.getContrastText(headerBgColor);

  const getHandleStyle = (position: 'left' | 'right'): React.CSSProperties => {
    const marginSpacing = -1.25;
    return {
      position: 'inherit',
      transform: 'inherit',
      ...(position === 'right'
        ? { marginRight: theme.spacing(marginSpacing) }
        : { marginLeft: theme.spacing(marginSpacing) }),
      height: theme.spacing(2),
      width: theme.spacing(Math.abs(marginSpacing)),
      borderRadius: 0,
      backgroundColor: theme.palette.common.black,
      border: 'none'
    };
  };

  return (
    <Stack direction='row'>
      <Card
        elevation={0}
        variant='outlined'
        sx={{
          minWidth: size?.width || '10rem',
          minHeight:
            size?.height || (props.sourceHandles.length <= 2 ? '8rem' : `${2 * props.sourceHandles.length}rem`),
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200],
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <CardHeader
          action={
            <IconButton tooltip='Details' onClick={data.onDetailsClicked}>
              <SettingsIcon sx={{ color: headerTextColor }} />
            </IconButton>
          }
          title={`${data.backendNode.type} - ${id}`}
          sx={{
            p: 0.5,
            color: headerTextColor,
            bgcolor: headerBgColor
          }}
        />
        <Stack py={1.5} px={1} gap={1} direction='row' alignItems='start' justifyContent='space-between' height='100%'>
          {!props.isStartNode && (
            <Handle
              id={getTargetId(id)}
              type={'target'}
              position={Position.Left}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEdge({ id: getTargetId(id), type: 'target' });
              }}
              style={getHandleStyle('left')}
            />
          )}
          <Typography variant='body1' sx={{ maxWidth: '10rem', flexGrow: 1 }}>
            {props.label}
          </Typography>
          {props.sourceHandles.some((handle) => !!handle.label) && (
            <>
              <Divider orientation='vertical' />
              <Stack alignItems='end' justifyContent='flex-start' height='100%' gap={1}>
                {props.sourceHandles.map((handle, index) => (
                  <Stack
                    key={`handles.stack.${id}.${handle.id}.${index}`}
                    direction='row'
                    height={17}
                    width='100%'
                    alignItems='center'
                    justifyContent='end'
                    gap={0.5}
                  >
                    <p
                      key={`labels.${id}.${handle.id}.${index}`}
                      style={{
                        border: '1px solid currentColor',
                        borderRadius: '0.4em',
                        padding: 4,
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        backgroundColor: theme.palette.background.default,
                        minHeight: theme.spacing(2),
                        minWidth: theme.spacing(2)
                      }}
                    >
                      {handle.label}
                    </p>
                    <Handle
                      key={`handles.${id}.${handle.id}.${index}`}
                      id={handle.id}
                      type={handle.type}
                      position={Position.Right}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEdge({ id: handle.id, type: handle.type });
                      }}
                      style={getHandleStyle('right')}
                    />
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default CustomNode;
